import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Zap,
  Users,
  Battery,
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';

const AdvancedAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [realTimeData, setRealTimeData] = useState({
    activeChargers: 0,
    activeSessions: 0,
    totalRevenue: 0,
    energyDelivered: 0,
    utilizationRate: 0,
    avgSessionDuration: 0,
    peakDemandTime: "N/A",
    alerts: 0
  });

  const [chartData, setChartData] = useState({
    revenue: [],
    usage: []
  });

  const [performanceMetrics, setPerformanceMetrics] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const loadAnalyticsData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        
        // Load dashboard stats
        const dashboardResponse = await fetch("/api/dashboard/stats-fixed", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (dashboardResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          setRealTimeData({
            activeChargers: dashboardData.activeChargers || 0,
            activeSessions: dashboardData.activeSessions || 0,
            totalRevenue: dashboardData.totalRevenue || 0,
            energyDelivered: 0,
            utilizationRate: dashboardData.totalChargers > 0 ? 
              (dashboardData.activeSessions / dashboardData.totalChargers * 100) : 0,
            avgSessionDuration: 0,
            peakDemandTime: "N/A",
            alerts: 0
          });
          
          // Load sessions for energy
          const sessionsResp = await fetch("/api/charging-sessions/history", {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (sessionsResp.ok) {
            const data = await sessionsResp.json();
            const sess = data.sessions || [];
            const energy = sess.reduce((sum, s) => sum + parseFloat(s.energy_consumed || 0), 0);
            const avgDur = sess.length > 0 ? sess.reduce((sum, s) => sum + (parseInt(s.duration) || 0), 0) / sess.length : 0;
            setRealTimeData(prev => ({ ...prev, energyDelivered: energy, avgSessionDuration: Math.round(avgDur) }));
          }
        }

        // Load real performance metrics
        const performanceResponse = await fetch("/api/analytics/charger-performance", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (performanceResponse.ok) {
          const performanceData = await performanceResponse.json();
          const metrics = performanceData.data.map(charger => ({
            id: charger.id,
            name: charger.name,
            uptime: parseFloat(charger.uptime) || 0,
            sessions: parseInt(charger.total_sessions) || 0,
            revenue: parseFloat(charger.total_revenue) || 0,
            efficiency: charger.total_sessions > 0 ? 90 : 0
          }));
          setPerformanceMetrics(metrics);
        }

        // Load real chart data
        const [revenueResponse, usageResponse] = await Promise.all([
          fetch("/api/analytics/revenue-trend?days=7", {
            headers: { "Authorization": `Bearer ${token}` }
          }),
          fetch("/api/analytics/usage-patterns", {
            headers: { "Authorization": `Bearer ${token}` }
          })
        ]);

        let newChartData = { revenue: [], usage: [] };
        
        if (revenueResponse.ok) {
          const revenueData = await revenueResponse.json();
          newChartData.revenue = revenueData.data || [];
        }
        
        if (usageResponse.ok) {
          const usageData = await usageResponse.json();
          newChartData.usage = usageData.data || [];
        }
        
        setChartData(newChartData);
        setAlerts([]); // No fake alerts

      } catch (error) {
        console.error("Failed to load analytics data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAnalyticsData();
    const interval = setInterval(loadAnalyticsData, 30000);
    return () => clearInterval(interval);
  }, []);

  const MetricCard = ({ title, value, change, icon: Icon, color = "blue" }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${change >= 0 ? "text-green-600" : "text-red-600"}`}>
              {change >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Advanced Analytics</h1>
              <p className="text-sm text-gray-600">Real-time insights and performance metrics</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button 
                onClick={() => window.location.reload()}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading analytics data...</span>
          </div>
        )}

        {/* Real-time Metrics */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <MetricCard
            title="Active Chargers"
            value={realTimeData.activeChargers}
            icon={Zap}
            color="green"
          />
          <MetricCard
            title="Active Sessions"
            value={realTimeData.activeSessions}
            icon={Activity}
            color="blue"
          />
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(realTimeData.totalRevenue)}
            icon={DollarSign}
            color="emerald"
          />
          <MetricCard
            title="Energy Delivered"
            value={`${realTimeData.energyDelivered.toFixed(1)} kWh`}
            icon={Battery}
            color="purple"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Revenue Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
            </div>
            {chartData.revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={256}>
                <AreaChart data={chartData.revenue.map(d => ({
                  date: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                  revenue: parseFloat(d.revenue),
                  sessions: parseInt(d.sessions)
                }))} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} tickFormatter={(v) => '£' + v.toFixed(0)} />
                  <Tooltip formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name === 'revenue' ? 'Revenue' : 'Sessions']} />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#D1FAE5" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">No revenue data available</div>
            )}
          </div>

          {/* Usage Pattern Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Usage Patterns</h3>
            </div>
            {chartData.usage.length > 0 ? (
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={chartData.usage.map(d => ({
                  hour: String(parseInt(d.hour)).padStart(2, '0') + ':00',
                  sessions: parseInt(d.session_count),
                  energy: parseFloat(d.total_energy)
                }))} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(value, name) => [name === 'energy' ? value.toFixed(1) + ' kWh' : value, name === 'energy' ? 'Energy' : 'Sessions']} />
                  <Bar dataKey="sessions" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">No usage data available</div>
            )}
          </div>
        </div>

        {/* Performance Metrics Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Charger Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Charger
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uptime
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {performanceMetrics.length > 0 ? performanceMetrics.map((metric) => (
                  <tr key={metric.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {metric.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {metric.sessions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(metric.revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {metric.uptime.toFixed(1)}%
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No performance data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalyticsDashboard;
